<template>
  <div class="calendar-range" :style="calendarRangeStyle">
    <CalendarMonthPane
      pane="left"
      :view="leftView"
      :transition-name="leftTransitionName"
      :grid-key="leftGridKey"
      :month-options="monthOptions"
      :year-options="yearOptions"
      :weekday-labels="weekdayLabels"
      :day-cells="leftDayCells"
      @navigate-month="onNavigateMonth"
      @set-month="onSetMonth"
      @set-year="onSetYear"
      @pick-day="onPickDay"
      @hover-day="onHoverDay"
    />

    <CalendarMonthPane
      pane="right"
      :view="rightView"
      :transition-name="rightTransitionName"
      :grid-key="rightGridKey"
      :month-options="monthOptions"
      :year-options="yearOptions"
      :weekday-labels="weekdayLabels"
      :day-cells="rightDayCells"
      @navigate-month="onNavigateMonth"
      @set-month="onSetMonth"
      @set-year="onSetYear"
      @pick-day="onPickDay"
      @hover-day="onHoverDay"
    />
  </div>
</template>

<script>
import { computed, defineComponent, ref, watch } from 'vue';
import { date } from 'quasar';
import { appSettings } from '@/Shared/appSettings.js';
import CalendarMonthPane from './CalendarMonthPane.vue';
import dayjs from 'dayjs';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const TRANSITION_FORWARD = 'month-slide-forward';
const TRANSITION_BACKWARD = 'month-slide-backward';
const TRANSITION_NONE = 'month-slide-none';

function utcDateFromDayKey(dayKey) {
  if (!dayKey || typeof dayKey !== 'string') return null;
  const parts = dayKey.split('-').map((v) => Number(v));
  if (parts.length !== 3 || parts.some((v) => Number.isNaN(v))) return null;
  return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
}

function dayKeyFromUtcDate(utcDate) {
  if (!(utcDate instanceof Date) || Number.isNaN(utcDate.getTime())) return null;
  return utcDate.toISOString().slice(0, 10);
}

function isoToDayKey(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString().slice(0, 10);
}

function dayKeyToIso(dayKey) {
  if (!dayKey) return null;
  return `${dayKey}T00:00:00.000Z`;
}

function compareDayKey(a, b) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function sameMonth(dayA, dayB) {
  return dayA && dayB && dayA.slice(0, 7) === dayB.slice(0, 7);
}

function compareView(a, b) {
  if (a.year !== b.year) return a.year < b.year ? -1 : 1;
  if (a.month !== b.month) return a.month < b.month ? -1 : 1;
  return 0;
}

function getTodayDayKey() {
  return new Date().toISOString().slice(0, 10);
}

export default defineComponent({
  name: 'CalendarRange',
  components: {
    CalendarMonthPane,
  },
  props: {
    startDate: {
      type: String,
      default: null,
    },
    endDate: {
      type: String,
      default: null,
    },
    preventFuture: {
      type: Boolean,
      default: false,
    },
    preventPast: {
      type: Boolean,
      default: false,
    },
    locale: {
      type: String,
      default: '',
    },
    paneGap: {
      type: [Number, String],
      default: null,
    },
    paddingLeft: {
      type: [Number, String],
      default: null,
    },
    paddingRight: {
      type: [Number, String],
      default: null,
    },
    paddingTop: {
      type: [Number, String],
      default: null,
    },
    paddingBottom: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['update:startDate', 'update:endDate', 'change'],
  setup(props, { emit }) {
    const resolvedLocale = computed(() => props.locale || appSettings.LocaleKey || navigator.language || 'en-US');
    const todayKey = computed(() => getTodayDayKey());

    const selectedStart = ref(isoToDayKey(props.startDate));
    const selectedEnd = ref(isoToDayKey(props.endDate));
    const hoveredDayKey = ref(null);

    if (selectedStart.value && selectedEnd.value && compareDayKey(selectedStart.value, selectedEnd.value) > 0) {
      const tmp = selectedStart.value;
      selectedStart.value = selectedEnd.value;
      selectedEnd.value = tmp;
    }

    const initialViews = deriveViews(selectedStart.value, selectedEnd.value, todayKey.value);
    const leftView = ref(initialViews.left);
    const rightView = ref(initialViews.right);
    const leftTransitionName = ref(TRANSITION_NONE);
    const rightTransitionName = ref(TRANSITION_NONE);
    const leftGridKey = computed(() => `${leftView.value.year}-${String(leftView.value.month).padStart(2, '0')}`);
    const rightGridKey = computed(() => `${rightView.value.year}-${String(rightView.value.month).padStart(2, '0')}`);

    // Our other calendars do not currently support this level of control over first day of week.
    // We will therefore use the same logic as in the other calendars to ensure consistency.
    // See formSingleDatePicker.jsx and formDate.jsx - Jeff Eardley 5/15/2026

    // const firstDayOfWeek = computed(() => inferFirstDayOfWeek(resolvedLocale.value));

    const firstDayOfWeek = computed(() => {
        try {
            return dayjs().startOf('week').day(); 
        } catch (e) {
            return 0;
        }
    });

    const weekdayLabels = computed(() => {
      const formatter = new Intl.DateTimeFormat(resolvedLocale.value, { weekday: 'short', timeZone: 'UTC' });
      const baseSunday = new Date(Date.UTC(2024, 0, 7));
      const labels = [];
      for (let i = 0; i < 7; i += 1) {
        labels.push(formatter.format(new Date(baseSunday.getTime() + (i * MS_PER_DAY))));
      }

      return rotateWeekdays(labels, firstDayOfWeek.value);
    });

    const monthOptions = computed(() => {
      const formatter = new Intl.DateTimeFormat(resolvedLocale.value, { month: 'long', timeZone: 'UTC' });
      const months = [];
      for (let month = 1; month <= 12; month += 1) {
        months.push({
          value: month,
          label: formatter.format(new Date(Date.UTC(2024, month - 1, 1))),
        });
      }
      return months;
    });

    const yearOptions = computed(() => {
      const baseYear = new Date().getUTCFullYear();
      let minYear = props.preventPast ? baseYear : baseYear - 100;
      let maxYear = props.preventFuture ? baseYear : baseYear + 100;

      const ensureYearIncluded = (dayKey) => {
        if (!dayKey) return;
        const year = Number(dayKey.slice(0, 4));
        if (!Number.isNaN(year)) {
          minYear = Math.min(minYear, year);
          maxYear = Math.max(maxYear, year);
        }
      };

      ensureYearIncluded(selectedStart.value);
      ensureYearIncluded(selectedEnd.value);
      minYear = Math.min(minYear, leftView.value.year, rightView.value.year);
      maxYear = Math.max(maxYear, leftView.value.year, rightView.value.year);

      const years = [];
      for (let year = minYear; year <= maxYear; year += 1) {
        years.push(year);
      }
      return years;
    });

    const leftDayCells = computed(() => buildMonthCells(leftView.value));
    const rightDayCells = computed(() => buildMonthCells(rightView.value));
    const calendarRangeStyle = computed(() => {
      const style = {};
      const paneGap = toCssUnit(props.paneGap);
      const paddingLeft = toCssUnit(props.paddingLeft);
      const paddingRight = toCssUnit(props.paddingRight);
      const paddingTop = toCssUnit(props.paddingTop);
      const paddingBottom = toCssUnit(props.paddingBottom);

      if (paneGap) style.gap = paneGap;
      if (paddingLeft) style.paddingLeft = paddingLeft;
      if (paddingRight) style.paddingRight = paddingRight;
      if (paddingTop) style.paddingTop = paddingTop;
      if (paddingBottom) style.paddingBottom = paddingBottom;
      return style;
    });

    watch(
      () => props.startDate,
      (iso) => {
        selectedStart.value = isoToDayKey(iso);
        normalizeSelectionOrder();
        hoveredDayKey.value = null;
        syncViewsFromSelection(false);
      }
    );

    watch(
      () => props.endDate,
      (iso) => {
        selectedEnd.value = isoToDayKey(iso);
        normalizeSelectionOrder();
        hoveredDayKey.value = null;
        syncViewsFromSelection(false);
      }
    );

    function normalizeSelectionOrder() {
      if (!selectedStart.value || !selectedEnd.value) return;
      if (compareDayKey(selectedStart.value, selectedEnd.value) > 0) {
        const swap = selectedStart.value;
        selectedStart.value = selectedEnd.value;
        selectedEnd.value = swap;
      }
    }

    function deriveViews(startKey, endKey, fallbackToday) {
      const fallback = fallbackToday || getTodayDayKey();

      if (startKey && endKey) {
        if (sameMonth(startKey, endKey)) {
          const right = dayKeyToView(endKey);
          const left = addMonthsToView(right, -1);
          return { left, right };
        }
        const startView = dayKeyToView(startKey);
        const endView = dayKeyToView(endKey);
        return compareView(startView, endView) <= 0
          ? { left: startView, right: endView }
          : { left: endView, right: startView };
      }

      if (startKey) {
        const left = dayKeyToView(startKey);
        return { left, right: addMonthsToView(left, 1) };
      }

      if (endKey) {
        const right = dayKeyToView(endKey);
        return { left: addMonthsToView(right, -1), right };
      }

      const right = dayKeyToView(fallback);
      return { left: addMonthsToView(right, -1), right };
    }

    function setTransitionForPane(pane, transitionName) {
      if (pane === 'left') {
        leftTransitionName.value = transitionName;
      } else {
        rightTransitionName.value = transitionName;
      }
    }

    function transitionFromCompare(fromView, toView) {
      const cmp = compareView(toView, fromView);
      if (cmp > 0) return TRANSITION_FORWARD;
      if (cmp < 0) return TRANSITION_BACKWARD;
      return TRANSITION_NONE;
    }

    function setPaneView(pane, nextView, options = {}) {
      const { animate = true, transitionName = null } = options;
      const currentView = pane === 'left' ? leftView.value : rightView.value;
      if (compareView(currentView, nextView) === 0) return false;

      const resolvedTransition = !animate
        ? TRANSITION_NONE
        : (transitionName || transitionFromCompare(currentView, nextView));
      setTransitionForPane(pane, resolvedTransition);

      if (pane === 'left') {
        leftView.value = { ...nextView };
      } else {
        rightView.value = { ...nextView };
      }
      return true;
    }

    function syncViewsFromSelection(animate = false) {
      if (selectedStart.value && !selectedEnd.value) {
        const startView = dayKeyToView(selectedStart.value);
        const isVisibleInLeft = compareView(startView, leftView.value) === 0;
        const isVisibleInRight = compareView(startView, rightView.value) === 0;
        if (isVisibleInLeft || isVisibleInRight) {
          enforcePaneOrder(undefined, animate);
          return;
        }
        setPaneView('left', startView, { animate });
        setPaneView('right', addMonthsToView(startView, 1), { animate });
        enforcePaneOrder(undefined, animate);
        return;
      }
      const views = deriveViews(selectedStart.value, selectedEnd.value, todayKey.value);
      setPaneView('left', views.left, { animate });
      setPaneView('right', views.right, { animate });
      enforcePaneOrder(undefined, animate);
    }

    function dayKeyToView(dayKey) {
      return {
        year: Number(dayKey.slice(0, 4)),
        month: Number(dayKey.slice(5, 7)),
      };
    }

    function addMonthsToView(view, delta) {
      // Avoid timezone/daylight-boundary drift by using integer month math.
      // This guarantees +/-1 month always advances exactly one month.
      const shiftedIndex = (view.year * 12) + (view.month - 1) + delta;
      const year = Math.floor(shiftedIndex / 12);
      const month = ((shiftedIndex % 12) + 12) % 12;
      return {
        year,
        month: month + 1,
      };
    }

    function enforcePaneOrder(changedPane, animate = true) {
      if (compareView(leftView.value, rightView.value) <= 0) return;

      if (changedPane === 'left') {
        setPaneView('right', leftView.value, { animate });
      } else if (changedPane === 'right') {
        setPaneView('left', rightView.value, { animate });
      } else {
        setPaneView('right', leftView.value, { animate });
      }
    }

    function isDayDisabled(dayKey) {
      if (!dayKey) return true;
      if (props.preventPast && compareDayKey(dayKey, todayKey.value) < 0) return true;
      if (props.preventFuture && compareDayKey(dayKey, todayKey.value) > 0) return true;
      return false;
    }

    function buildMonthCells(view) {
      const firstDay = new Date(Date.UTC(view.year, view.month - 1, 1));
      const firstWeekday = firstDay.getUTCDay();
      const offset = (firstWeekday - firstDayOfWeek.value + 7) % 7;
      const monthLength = new Date(Date.UTC(view.year, view.month, 0)).getUTCDate();
      const previousMonthLength = new Date(Date.UTC(view.year, view.month - 1, 0)).getUTCDate();

      const cells = [];
      const { fromKey, toKey } = getEffectiveRangeBounds();

      for (let i = 0; i < offset; i += 1) {
        const day = (previousMonthLength - offset) + i + 1;
        const utcDate = new Date(Date.UTC(view.year, view.month - 2, day));
        const dayKey = dayKeyFromUtcDate(utcDate);
        const isStart = selectedStart.value === dayKey;
        const isEnd = selectedEnd.value === dayKey;
        const hasRange = Boolean(fromKey && toKey);
        const isInRange = hasRange
          ? date.isBetweenDates(utcDate, utcDateFromDayKey(fromKey), utcDateFromDayKey(toKey), {
            inclusiveFrom: true,
            inclusiveTo: true,
            onlyDate: true,
          })
          : false;

        cells.push({
          id: dayKey,
          isPlaceholder: false,
          isOutsideMonth: true,
          label: String(day),
          dayKey,
          isDisabled: isDayDisabled(dayKey),
          isToday: dayKey === todayKey.value,
          isInRange,
          isStart,
          isEnd,
          isRangeStartEdge: hasRange && fromKey === dayKey,
          isRangeEndEdge: hasRange && toKey === dayKey,
          isPreviewEdge: !selectedEnd.value && hoveredDayKey.value && (dayKey === hoveredDayKey.value),
          hasRangeBand: hasRange,
        });
      }

      for (let day = 1; day <= monthLength; day += 1) {
        const utcDate = new Date(Date.UTC(view.year, view.month - 1, day));
        const dayKey = dayKeyFromUtcDate(utcDate);
        const isStart = selectedStart.value === dayKey;
        const isEnd = selectedEnd.value === dayKey;
        const hasRange = Boolean(fromKey && toKey);
        const isInRange = hasRange
          ? date.isBetweenDates(utcDate, utcDateFromDayKey(fromKey), utcDateFromDayKey(toKey), {
            inclusiveFrom: true,
            inclusiveTo: true,
            onlyDate: true,
          })
          : false;
        const isRangeStartEdge = hasRange && fromKey === dayKey;
        const isRangeEndEdge = hasRange && toKey === dayKey;
        const isPreviewEdge = !selectedEnd.value && hoveredDayKey.value && (dayKey === hoveredDayKey.value);

        cells.push({
          id: dayKey,
          isPlaceholder: false,
          isOutsideMonth: false,
          label: String(day),
          dayKey,
          isDisabled: isDayDisabled(dayKey),
          isToday: dayKey === todayKey.value,
          isInRange,
          isStart,
          isEnd,
          isRangeStartEdge,
          isRangeEndEdge,
          isPreviewEdge,
          hasRangeBand: hasRange,
        });
      }

      const totalRows = (offset + monthLength) <= 35 ? 5 : 6;
      const totalCells = totalRows * 7;

      while (cells.length < totalCells) {
        const nextMonthDay = (cells.length - (offset + monthLength)) + 1;
        const utcDate = new Date(Date.UTC(view.year, view.month, nextMonthDay));
        const dayKey = dayKeyFromUtcDate(utcDate);
        const isStart = selectedStart.value === dayKey;
        const isEnd = selectedEnd.value === dayKey;
        const hasRange = Boolean(fromKey && toKey);
        const isInRange = hasRange
          ? date.isBetweenDates(utcDate, utcDateFromDayKey(fromKey), utcDateFromDayKey(toKey), {
            inclusiveFrom: true,
            inclusiveTo: true,
            onlyDate: true,
          })
          : false;

        cells.push({
          id: dayKey,
          isPlaceholder: false,
          isOutsideMonth: true,
          label: String(nextMonthDay),
          dayKey,
          isDisabled: isDayDisabled(dayKey),
          isToday: dayKey === todayKey.value,
          isInRange,
          isStart,
          isEnd,
          isRangeStartEdge: hasRange && fromKey === dayKey,
          isRangeEndEdge: hasRange && toKey === dayKey,
          isPreviewEdge: !selectedEnd.value && hoveredDayKey.value && (dayKey === hoveredDayKey.value),
          hasRangeBand: hasRange,
        });
      }

      return cells;
    }

    function emitRange() {
      const payload = {
        startDate: dayKeyToIso(selectedStart.value),
        endDate: dayKeyToIso(selectedEnd.value),
      };
      emit('update:startDate', payload.startDate);
      emit('update:endDate', payload.endDate);
      emit('change', payload);
    }

    function onPickDay({ dayKey }) {
      if (!dayKey || isDayDisabled(dayKey)) return;

      if (!selectedStart.value || (selectedStart.value && selectedEnd.value)) {
        selectedStart.value = dayKey;
        selectedEnd.value = null;
        hoveredDayKey.value = null;
        emitRange();
        return;
      }

      if (compareDayKey(dayKey, selectedStart.value) < 0) {
        selectedEnd.value = selectedStart.value;
        selectedStart.value = dayKey;
      } else {
        selectedEnd.value = dayKey;
      }

      hoveredDayKey.value = null;
      emitRange();
      normalizeVisibleMonthsForSelection();
    }

    function getEffectiveRangeBounds() {
      if (selectedStart.value && selectedEnd.value) {
        return { fromKey: selectedStart.value, toKey: selectedEnd.value };
      }

      if (selectedStart.value && hoveredDayKey.value) {
        if (compareDayKey(hoveredDayKey.value, selectedStart.value) < 0) {
          return { fromKey: hoveredDayKey.value, toKey: selectedStart.value };
        }
        return { fromKey: selectedStart.value, toKey: hoveredDayKey.value };
      }

      return { fromKey: null, toKey: null };
    }

    function onHoverDay({ dayKey, isDisabled }) {
      if (!selectedStart.value || selectedEnd.value) {
        hoveredDayKey.value = null;
        return;
      }

      if (!dayKey || isDisabled) {
        hoveredDayKey.value = null;
        return;
      }

      hoveredDayKey.value = dayKey;
    }

    function normalizeVisibleMonthsForSelection() {
      if (!selectedStart.value || !selectedEnd.value) return;
      if (sameMonth(selectedStart.value, selectedEnd.value)) {
        const nextRightView = dayKeyToView(selectedEnd.value);
        setPaneView('right', nextRightView, { animate: false });
        setPaneView('left', addMonthsToView(nextRightView, -1), { animate: false });
      } else {
        setPaneView('left', dayKeyToView(selectedStart.value), { animate: false });
        setPaneView('right', dayKeyToView(selectedEnd.value), { animate: false });
      }
      enforcePaneOrder(undefined, false);
    }

    function onNavigateMonth({ pane, delta }) {
      const transitionName = delta >= 0 ? TRANSITION_FORWARD : TRANSITION_BACKWARD;
      if (pane === 'left') {
        setPaneView('left', addMonthsToView(leftView.value, delta), { transitionName });
        enforcePaneOrder('left', true);
      } else {
        setPaneView('right', addMonthsToView(rightView.value, delta), { transitionName });
        enforcePaneOrder('right', true);
      }
    }

    function onSetMonth({ pane, month }) {
      if (pane === 'left') {
        setPaneView('left', { ...leftView.value, month }, { animate: true });
        enforcePaneOrder('left', true);
      } else {
        setPaneView('right', { ...rightView.value, month }, { animate: true });
        enforcePaneOrder('right', true);
      }
    }

    function onSetYear({ pane, year }) {
      if (pane === 'left') {
        setPaneView('left', { ...leftView.value, year }, { animate: true });
        enforcePaneOrder('left', true);
      } else {
        setPaneView('right', { ...rightView.value, year }, { animate: true });
        enforcePaneOrder('right', true);
      }
    }

    return {
      leftView,
      rightView,
      leftTransitionName,
      rightTransitionName,
      leftGridKey,
      rightGridKey,
      monthOptions,
      yearOptions,
      weekdayLabels,
      leftDayCells,
      rightDayCells,
      calendarRangeStyle,
      onNavigateMonth,
      onSetMonth,
      onSetYear,
      onPickDay,
      onHoverDay,
    };
  },
});

function rotateWeekdays(weekdayLabels, firstDay) {
  const start = Math.min(6, Math.max(0, Number(firstDay) || 0));
  return [...weekdayLabels.slice(start), ...weekdayLabels.slice(0, start)];
}

function inferFirstDayOfWeek(locale) {
  try {
    const intlLocale = new Intl.Locale(locale);
    if (intlLocale && intlLocale.weekInfo && intlLocale.weekInfo.firstDay) {
      const first = intlLocale.weekInfo.firstDay;
      if (first === 7) return 0;
      return first - 1;
    }
  } catch (error) {
    // Fallback below.
  }

  const normalized = String(locale || '').toLowerCase();
  const sundayRegions = ['us', 'ph', 'jp', 'tw', 'hk', 'cn', 'th', 'kr', 'ca', 'au', 'nz'];
  const region = normalized.includes('-') ? normalized.split('-')[1] : '';
  if (region && sundayRegions.includes(region)) return 0;
  return 1;
}

function toCssUnit(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}px`;
  const normalized = String(value).trim();
  if (!normalized) return null;
  return /^\d+(\.\d+)?$/.test(normalized) ? `${normalized}px` : normalized;
}
</script>

<style scoped>
.calendar-range {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
}
</style>
